import {DragEndEvent} from '@dnd-kit/core';
import {Button, FormControl} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {
  buildSortableItemId,
  DnDContext,
  parseDndItemId,
  Sortable,
  SortableWrapper,
} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {ChecklistFormValue} from '../types/ChecklistFormValue';
import {checklistTaskFactory} from '../utils/checklistTaskFactory';
import {TaskCard} from './TaskCard';

const CONTAINER_ID = 'checklist-tasks';

interface TasksControlProps {
  formApi: UseFormReturn<ChecklistFormValue>;
  control: FormControl<ChecklistFormValue>;
}

export function TasksControl(props: TasksControlProps) {
  const taskControl = useFieldArray({
    control: props.formApi.control,
    name: 'tasks',
  });

  const onDragEnd = (event: DragEndEvent) => {
    if (isNil(event.over) || event.active.id === event.over.id) {
      return;
    }

    const fromIndex = parseDndItemId(event.active.id).position;
    const toIndex = parseDndItemId(event.over.id).position;

    if (isNotNil(fromIndex) && isNotNil(toIndex)) {
      taskControl.move(fromIndex, toIndex);
    }
  };

  return (
    <VStack spacing={6}>
      <DnDContext onDragEnd={onDragEnd}>
        <SortableWrapper
          containerId={CONTAINER_ID}
          items={taskControl.fields.map((field) => ({id: field.id, url: field.id}))}
        >
          {taskControl.fields.map((field, index) => (
            <Sortable
              key={field.id}
              id={buildSortableItemId(field.id, CONTAINER_ID, field.id, index)}
              withHandle
            >
              {({handleProps}) => (
                <TaskCard
                  control={props.control}
                  formApi={props.formApi}
                  handleProps={handleProps}
                  handleRemove={() => taskControl.remove(index)}
                  index={index}
                  isAlone={taskControl.fields.length === 1}
                />
              )}
            </Sortable>
          ))}
        </SortableWrapper>
      </DnDContext>
      <Button
        data-testid={testIds.settings.generalChecklistCreate('addTask')}
        leftIcon="content/add_circle"
        onClick={() => taskControl.append(checklistTaskFactory())}
        title={i18n.t('entity.settings.labels.addTask')}
        variant="ghostLink"
      />
    </VStack>
  );
}
